import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAccountData } from "../../../hooks/useAccountData";
import {
  GET_SUBSCRIPTION_PRICING_V2_QUERY_KEY,
  getSubscriptionPricingV2,
} from "../api/paymentApi";
import { PaywallV2Type, SubscriptionOfferV2Type } from "../types";

export function useSubscriptionPricingV2(appliedPromoCode: string) {
  const accountData = useAccountData();

  const { data } = useQuery({
    enabled: !!accountData,
    queryKey: [...GET_SUBSCRIPTION_PRICING_V2_QUERY_KEY, appliedPromoCode],
    queryFn: () =>
      getSubscriptionPricingV2(appliedPromoCode, accountData?.user_offer ?? ""),
    refetchOnWindowFocus: false,
  });

  const [yearlySub, setYearlySub] = useState<SubscriptionOfferV2Type>();
  const [monthlySub, setMonthlySub] = useState<SubscriptionOfferV2Type>();
  const [activeType, setActiveType] = useState<"yearly" | "monthly">();
  const [paywall, setPaywall] = useState<PaywallV2Type>();

  useEffect(() => {
    const results = data?.data.payload.results;
    const subscriptions = results?.subscriptions;

    if (!subscriptions) return;

    setYearlySub(
      subscriptions.find((sub) => sub.billing_frequency === "yearly"),
    );
    setMonthlySub(
      subscriptions.find((sub) => sub.billing_frequency === "monthly"),
    );
    setPaywall(results?.paywall);
  }, [data]);

  useEffect(() => {
    if (!activeType || !appliedPromoCode) {
      if (yearlySub?.promo_code) {
        setActiveType("yearly");
      } else if (monthlySub?.promo_code) {
        setActiveType("monthly");
      } else {
        setActiveType("yearly");
      }
    } else {
      if (appliedPromoCode === yearlySub?.promo_code) {
        setActiveType("yearly");
      } else if (appliedPromoCode === monthlySub?.promo_code) {
        setActiveType("monthly");
      }
    }
  }, [yearlySub, monthlySub]);

  return {
    yearlySub,
    monthlySub,
    activeType,
    setActiveType,
    paywall,
    data,
  };
}
